import { doc, updateDoc } from 'firebase/firestore';
import { getBlob, getDownloadURL, ref } from 'firebase/storage';
import React, { useEffect, useContext, useState } from 'react';
import { AppContext } from '../../../../AppContext';
import constants from '../../../../constants';
import { db, storage } from '../../../../firebase';
import icons from '../../../../icons';
import IconButton from '../../../Buttons/IconButton/IconButton';
import TextButton from '../../../Buttons/TextButton/TextButton';
import Checkbox from '../../../Checkbox/Checkbox';
import style from './Events.module.css';


const literalGrades = [
    "0", "0+", "0½", "1-",
    "1", "1+", "1½", "2-",
    "2", "2+", "2½", "3-",
    "3", "3+", "3½", "4-",
    "4", "4+", "4½", "5-",
    "5", "5+", "5½", "6-",
    "6", "6+", "6½", "7-",
    "7", "7+", "7½", "8-",
    "8", "8+", "8½", "9-",
    "9", "9+", "9½", "10-",
    "10", "10+"
];

const numericGrades = [
    0, 0.3, 0.5, 0.8,
    1, 1.3, 1.5, 1.8,
    2, 2.3, 2.5, 2.8,
    3, 3.3, 3.5, 3.8,
    4, 4.3, 4.5, 4.8,
    5, 5.3, 5.5, 5.8,
    6, 6.3, 6.5, 6.8,
    7, 7.3, 7.5, 7.8,
    8, 8.3, 8.5, 8.8,
    9, 9.3, 9.5, 9.8,
    10, 10

];

const getLiteralGrade = numericGrade => {
    if (numericGrade === null) return null;
    return literalGrades[numericGrades.indexOf(parseFloat(numericGrade))];
}

const getNumericGrade = literalGrade => {
    if (literalGrade === null) return null;
    return numericGrades[literalGrades.indexOf(literalGrade)];
}

const Events = props => {
    const { users, user, subjects, setPage, setTests, setInterrogations, setHomeworks, setOthers } = useContext(AppContext);

    const { EVENTS_FILTER_ALL, EVENTS_FILTER_FOR_YOU } = constants;
    const [filter, setFilter] = useState(EVENTS_FILTER_FOR_YOU);
    
    const getItalianWeekDay = weekDay => {
        switch (weekDay) {
            case 0:
                return "Domenica";
            case 1:
                return "Lunedì";
            case 2:
                return "Martedì";
            case 3:
                return "Mercoledì";
            case 4:
                return "Giovedì";
            case 5:
                return "Venerdì";
            case 6:
                return "Sabato";
        }
    };

    const getItalianMonth = month => {
        switch(month) {
            case 0:
                return "gennaio";
            case 1:
                return "febbraio";
            case 2:
                return "marzo";
            case 3:
                return "aprile";
            case 4:
                return "maggio";
            case 5:
                return "giugno";
            case 6:
                return "luglio";
            case 7:
                return "agosto";
            case 8:
                return "settembre";
            case 9:
                return "ottobre";
            case 10:
                return "novembre";
            case 11:
                return "dicembre";
        }
    };

    const getItalianType = eventType => {
        switch (eventType) {
            case "test":
                return "Verifica";
            case "interrogation":
                return "Orale";
            case "homework":
                return "Per casa";
            case "other":
                return "Altro";
            default:
                return "Scegli";
        }
    };

    const getTypeIcon = (eventType, disabled=false) => {
        switch (eventType) {
            case "test":
                return disabled ? icons.test_disabled_icon : icons.test_icon;
            case "interrogation":
                return disabled ? icons.interrogation_disabled_icon : icons.interrogation_icon;
            case "homework":
                return disabled ? icons.homework_disabled_icon : icons.home_orange_icon;
            default:
                return disabled ? icons.question_mark_orange_disabled_icon : icons.question_mark_orange_icon;
        }
    };

    const getAttachmentIcon = attachmentType => {
        switch (true) {
            case attachmentType.includes("txt"):
                return icons.word_icon;
            case attachmentType.includes("doc"):
                return icons.word_icon;
            case attachmentType.includes("png"):
                return icons.photo_icon;
            case attachmentType.includes("jpg"):
                return icons.photo_icon;
            case attachmentType.includes("webp"):
                return icons.photo_icon;
            case attachmentType.includes("xlsx"):
                return icons.excel_icon;
            default:
                return icons.question_mark_orange_icon;
        }
    };

    const getUserAvatarByName = name => {
        return `https://eu.ui-avatars.com/api/?name=${name}&background=d86c07&rounded=true&format=svg&color=fff`;
    };

    const getEventCreator = event => {
        if (!users)
            return null;

        return Object.values(users).filter(dataUser => dataUser.id === event.addedBy)?.[0] ?? null;
    };

    const getEventCreatorProfilePic = event => {
        if (!users)
            return icons.cartoon_avatar_icon;

        return getEventCreator(event) ? getEventCreator(event).profilePicture || getUserAvatarByName(getEventCreator(event).username) : icons.cartoon_avatar_icon;
    };

    const getEventUsers = (event, excludeCreator=true) => {
        if (!users)
            return [];

        return (excludeCreator ? Object.values(users).filter(eventUser => eventUser.id !== getEventCreator(event)?.id) : Object.values(users)).filter(dataUser => event.for.length === 0 || event.for.includes(dataUser.id));
    };

    const setChecked = async (event, done) => {
        if (!user)
            return false;

        const updateEvent = () => {
            const setState = event.type === "test" ? setTests : event.type === "interrogation" ? setInterrogations : event.type === "homework" ? setHomeworks : setOthers;
            setState(oldState => ({
                ...oldState,
                [event.id]: {
                    ...event,
                    done: {
                        ...event.done,
                        [user.id]: done
                    }
                }
            }));
        }

        updateEvent(event.type);
        await updateDoc(doc(db, `schools/${user?.school}/classes/${user?.class}/${event.type}s/`, event.id), { [`done.${user.id}`]: done });
    };

    const getSubjectIcon = subjectName => {
        let returnIcon = "question_mark_orange_icon";
        if (subjects)
            Object.values(subjects).forEach(subject => {
                if (subject.subject.toLowerCase() === subjectName?.toLowerCase()) returnIcon = subject.icon;
            });

        return returnIcon;
    };

    const editEvent = event => {
        setPage({ page: "add", event });
    };

    useEffect(() => {
        if (props.selected) {
            if (props.mainContent)
                props.mainContent.current.style.overflow = "hidden";
        } else {
            if (props.mainContent)
                props.mainContent.current.style.overflowY = "scroll";
        }    
    }, [props.selected]);

    return <main className={ style.main } style={{
        transitionProperty: !props.selected ? "width, height, top, left, opacity" : "width, height, top, left",
        minHeight: props.selected ? "105vh" : "1vh",
        height: props.selected ? "105vh" : "1vh",
        width: props.selected ? "100vw" : "1vw",
        top: props.selected ? "0" : "42vh",
        left: props.selected ? "0" : "19.75vw",
        opacity: props.selected ? "1" : "0",
    }}>
        <section className={ style.topSection }>
            <div className={ style.decoration }></div>
            <div className={ style.flex }>
                <img src={ icons.arrow_left_icon } className={ style.backIcon } onClick={ props.hide }></img>
                <h2 className={ style.subject }>{ props.lesson?.subjectTitle }</h2>
                {/* <img src={ icons.edit_white_icon } className={ style.editIcon }></img> */}
            </div>
            <h3 className={ style.time }>{ `${props.lesson?.startTime} - ${props.lesson?.endTime}` }</h3>
        </section>
        <section className={ style.mainContent }>
            <div className={ style.flex }>
                <div style={{ flex: 1 }}>
                    <h3 className={ style.eventsHeader }>Eventi</h3>
                    <h4 className={ style.eventsSubheader }>{getItalianWeekDay(props.date.getDay())} {props.date.getDate()} {getItalianMonth(props.date.getMonth())}</h4>
                </div>
                <IconButton icon={ icons.add_icon } height="100%" iconDimensions="1rem" fontSize=".85rem" text="Evento" style={{ marginTop: "1.75rem", marginRight: "1rem", width: "fit-content", padding: "0" }} iconStyle={{ marginRight: "1rem" }} textStyle={{ marginRight: "1rem" }} onClick={ () => setPage({ page: "add", event: { subject: props.events[0]?.subject, date: props.events[0]?.startDate ?? props.events[0]?.date } }) } />
            </div>
            <div className={ style.filters }>
                <div className={ [style.filter, filter === EVENTS_FILTER_FOR_YOU && style.selected].join(" ") } onClick={ () => setFilter(EVENTS_FILTER_FOR_YOU) }>
                    <h4>Per te</h4>
                </div>
                <div className={ [style.filter, filter === EVENTS_FILTER_ALL && style.selected].join(" ") } onClick={ () => setFilter(EVENTS_FILTER_ALL) }>
                    <h4>Tutti</h4>
                </div>
            </div>
            <div className={ style.events }>
                { (filter === EVENTS_FILTER_ALL ? props.events : props.events.filter(event => event.for.length === 0 || event.for.includes(user?.id))).map(event => <div key={ `event-${event.id}` } className={ style.event }>
                    { props.lesson?.subjectTitle !== "Altro" ? <div className={ style.flex }>
                        <img src={ event.done?.[user?.id] ? getTypeIcon(event.type, true) : getTypeIcon(event.type) } className={ style.eventTypeIcon } onClick={ () => editEvent(event) }></img>
                        <h3 className={ style.eventType } style={{ textDecoration: event.done?.[user?.id] ? "line-through 2px" : "none" }} onClick={ () => editEvent(event) }>{ getItalianType(event.type) }</h3>
                        { event.type !== "other" && getLiteralGrade(event.grades?.[user?.id]?.grade) && event.grades?.[user?.id] && <h4 className={ style.grade } style={{ background: event.grades?.[user.id]?.grade && (!event.grades?.[user.id]?.inGradeAverage ? "rgb(0, 99, 248)" : parseFloat(event.grades?.[user.id]?.grade) >= 6 ? "rgb(0, 128, 0)" : parseFloat(event.grades?.[user.id]?.grade) >= 5.5 ? "rgb(255, 166, 0)" : "rgb(255, 0, 0)") }} onClick={ () => editEvent(event) }>{ getLiteralGrade(event.grades?.[user?.id]?.grade) }</h4> }
                        <Checkbox checked={ event.done?.[user?.id] } onChange={ e => setChecked(event, e.target.checked) } id={ event.id } style={{ marginTop: ".5rem", zIndex: "100"  }} />
                    </div> : 
                    <div className={ style.flex }>
                        <img src={ icons[getSubjectIcon(event.subject)] } className={ style.eventTypeIcon } onClick={ () => editEvent(event) }></img>
                        <div className={ style.otherEventHeaders }>
                            <h3 className={ style.otherEventHeader } style={{ textDecoration: event.done?.[user?.id] ? "line-through 2px" : "none" }}>{ event.subject }</h3>
                            <h3 className={ style.otherEventHeader } style={{ fontSize: ".9rem", color: "var(--orange)", textDecoration: event.done?.[user?.id] ? "line-through 2px" : "none" }}>{ getItalianType(event.type) }</h3>
                        </div>
                        { event.type !== "other" && event.grades?.[user?.id] && getLiteralGrade(event.grades?.[user?.id]?.grade) && <h4 className={ style.grade } style={{ background: event.grades?.[user.id]?.grade && (!event.grades?.[user.id]?.inGradeAverage ? "rgb(0, 99, 248)" : parseFloat(event.grades?.[user.id]?.grade) >= 6 ? "rgb(0, 128, 0)" : parseFloat(event.grades?.[user.id]?.grade) >= 5.5 ? "rgb(255, 166, 0)" : "rgb(255, 0, 0)") }} onClick={ () => editEvent(event) }>{ getLiteralGrade(event.grades?.[user?.id]?.grade) }</h4> }
                        <Checkbox checked={ event.done?.[user?.id] } onChange={ e => setChecked(event, e.target.checked) } id={ event.id } style={{ marginTop: ".5rem", zIndex: "100"  }} />
                    </div> }
                    <div onClick={ () => editEvent(event) }>
                        <h4 className={ style.eventDescription } style={{ textDecoration: event.done?.[user?.id] ? "line-through 2px" : "none" }}>{ event.title }</h4>
                        <div className={ style.attachments }>
                            { event.attachments?.map(attachment => <div className={ style.attachment } key={ `attachment-${attachment}` }>
                                <img src={ getAttachmentIcon(attachment.split(".")[1]) } className={ style.attachmentTypeIcon }></img>
                                <h5 className={ style.attachmentName }>{ attachment.split(".").slice(0, attachment.split(".").length - 1) }</h5>
                                <img src={ icons.download_icon } className={ style.downloadIcon } onClick={ async e => {
                                    e.stopPropagation();

                                    const url = await getDownloadURL(ref(storage, `${user.school}/${user.class}/${event.type}s/${event.id}/${attachment}`));
                                    const a = document.createElement('a');

                                    a.href = url;
                                    a.click();
                                    a.remove();
                                } }></img>
                            </div>) }
                        </div>
                        <div className={ style.flex } style={{ marginLeft: "1rem" }}>
                            <h5 className={ style.usersHeader } style={{ textDecoration: event.done?.[user?.id] ? "line-through 2px" : "none" }}>Creato da:</h5>
                            <img src={ getEventCreatorProfilePic(event) } className={ style.eventUserCreator }></img>
                        </div>
                        <div className={ style.flex } style={{ marginLeft: "1rem", marginTop: ".5rem" }}>
                            <h5 className={ style.usersHeader } style={{ textDecoration: event.done?.[user?.id] ? "line-through 2px" : "none" }}>Assegnato a:</h5>
                            <div className={ style.eventUsers }>
                                { getEventUsers(event).slice(0, 5).map(eventUser => <img key={ `profilePic-${eventUser.id}` } src={ eventUser.profilePicture || getUserAvatarByName(eventUser.username) } referrerPolicy="no-referrer" className={ style.eventUser }></img>) }
                                { getEventUsers(event).length > 5 && <span className={ style.eventUsersMore }>+{ getEventUsers(event).length - 5 }</span> }
                            </div>
                        </div>
                    </div>
                </div>) }
                {/* <div className={ style.event }>
                    <div className={ style.flex }>
                        <img src={ icons.home_orange_icon } className={ style.eventTypeIcon }></img>
                        <h3 className={ style.eventType }>Compito per casa</h3>
                        <Checkbox id={ "eventId" } style={{ marginTop: ".5rem" }} />
                    </div>
                    <h4 className={ style.eventDescription }>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</h4>
                    <div className={ style.flex } style={{ marginLeft: "1rem" }}>
                        <h5 className={ style.usersHeader }>Creato da:</h5>
                        <img src={ icons.cartoon_avatar_icon } className={ style.eventUserCreator }></img>
                    </div>
                    <div className={ style.flex } style={{ marginLeft: "1rem", marginTop: ".5rem" }}>
                        <h5 className={ style.usersHeader }>Assegnato a:</h5>
                        <div className={ style.eventUsers }>
                            <img src={ icons.cartoon_avatar_icon } className={ style.eventUser }></img>
                            <img src={ icons.cartoon_avatar_icon } className={ style.eventUser }></img>
                            <img src={ icons.cartoon_avatar_icon } className={ style.eventUser }></img>
                            <img src={ icons.cartoon_avatar_icon } className={ style.eventUser }></img>
                            <span className={ style.eventUsersMore }></span>
                        </div>
                    </div>
                </div> */}
            </div>            
        </section>
    </main>
};

export default Events;
